/**
 * Email auto actions on/off (#3739). When on, OS Buddy links a classified
 * mailbox email's attachments to the one open work order its TM number, plate
 * or VIN matches, and an unlinked thread to the one customer with the sender's
 * address. Off by default; staff can undo each action in Messages.
 */

import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useEmailAutoActionsSetting, useSaveEmailAutoActionsSetting } from '@/hooks/useInboundDocuments';
import { useNotify } from '@/hooks/useNotify';

export function EmailAutoActionsSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const query = useEmailAutoActionsSetting();
  const save = useSaveEmailAutoActionsSetting();

  if (!query.data) return null;

  const toggle = async (enabled: boolean) => {
    try {
      await save.mutateAsync(enabled);
      notify.success(t('inboundEmail.autoActions.saved'));
    } catch {
      notify.error(tc('error'), t('inboundEmail.autoActions.saveFailed'));
    }
  };

  return (
    <Stack spacing={1} data-testid="email-auto-actions-settings">
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('inboundEmail.autoActions.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inboundEmail.autoActions.description')}
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={query.data.enabled}
            onChange={(event) => { void toggle(event.target.checked); }}
            disabled={!canEdit || save.isPending}
          />
        }
        label={t('inboundEmail.autoActions.enabledLabel')}
      />
    </Stack>
  );
}
